import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import CreateDepartmentModal from '../Modals/CreateDepartmentModal';
import EditDepartmentModal from '../Modals/EditDepartmentModal';
import RecordDetailsModal from '../shared/RecordDetailsModal';
import RegistryToolbar from './SpeciesLivestockRegistry/RegistryToolbar';
import { departmentsApi, buildParentDepartmentOptions, DEPARTMENT_CATEGORY_OPTIONS } from '../../../api/departmentsApi';
import { actionMenuHtml, humanizedCellHtml, parseApiError, textCellHtml } from './SpeciesLivestockRegistry/registryHelpers';
import SystemRowMenu, { standardRowItems, confirmAction } from '../shared/SystemRowMenu';
import { anchorRect } from '../../shared/useAnchoredMenu';

const CATEGORY_TONES = {
  department: 'background:#dbeafe;color:#1d4ed8;border:1px solid #bfdbfe;',
  unit: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
};

function categoryBadgeHtml(value, type) {
  if (type !== 'display') return value ?? '';
  const key = String(value ?? '').toLowerCase();
  const style = CATEGORY_TONES[key] || 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;';
  const label = key === 'unit' ? 'Units' : 'Department';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${label}</span>`;
}

const COLUMNS = [
  { title: 'Name',            data: 'name',           sortable: true, render: textCellHtml },
  { title: 'Category',        data: 'category',       sortable: true, render: categoryBadgeHtml },
  { title: 'Department Name', data: 'departmentName',  sortable: true, render: humanizedCellHtml },
  { title: 'Action', data: null, sortable: false, render: (_d, type) => (type === 'display' ? actionMenuHtml() : '') },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All Departments' },
  ...DEPARTMENT_CATEGORY_OPTIONS,
];

export default function DepartmentRegistry() {
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    departmentsApi.listAll()
      .then(({ items }) => { if (active) setRecords(items); })
      .catch((err) => { if (active) setError(parseApiError(err, 'Failed to load departments.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshTick]);

  const parentOptions = useMemo(() => buildParentDepartmentOptions(records), [records]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return records.filter((row) => {
      const matchSearch = !q || `${row.name} ${row.departmentName}`.toLowerCase().includes(q);
      const matchCategory = !categoryFilter || row.category === categoryFilter;
      return matchSearch && matchCategory;
    });
  }, [records, searchQuery, categoryFilter]);

  const handleSearch = () => setSearchQuery(searchDraft.trim());
  const handleRefresh = () => setRefreshTick((v) => v + 1);

  const handleCreate = async (payload) => {
    await departmentsApi.create(payload);
    toast.success('Record created.', { position: 'top-right' });
    handleRefresh();
  };

  const handleSave = async (payload) => {
    if (!payload?.id) throw new Error('Missing record identifier.');
    await departmentsApi.update(payload.id, payload);
    toast.success('Record updated.', { position: 'top-right' });
    handleRefresh();
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    const label = row.name || 'this record';
    if (!(await confirmAction({ title: 'Delete Record?', message: `Delete ${label}?`, confirmLabel: 'Delete', danger: true }))) return;
    setDeleting(true);
    try {
      await departmentsApi.remove(row.id);
      setViewRow(null);
      toast.success('Record removed.', { position: 'top-right' });
      handleRefresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Unable to delete record.'), { position: 'top-right' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = (action, row, meta) => {
    if (!row) return;
    if (action === 'menu') { setRowMenu({ row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'edit') setEditRow(row);
  };

  const categoryLabel = CATEGORY_FILTER_OPTIONS.find((o) => o.value === categoryFilter)?.label ?? 'All Departments';

  return (
    <section>
      <RegistryToolbar
        search={searchDraft}
        setSearch={setSearchDraft}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
        createLabel="Create New"
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-gray-700">Filter by Department Category:</span>
        <div className="relative">
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-9 text-sm text-gray-700 focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-400"
          >
            {CATEGORY_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <span className="text-sm italic text-gray-400">Showing: {categoryLabel}</span>
      </div>

      {error && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <button type="button" onClick={handleRefresh}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && (
        <p className="mb-3 text-sm text-gray-500">Loading departments...</p>
      )}

      <NlimsDataTable
        tableId="department-registry-table"
        columns={COLUMNS}
        data={filtered}
        serverSide={false}
        hideSearch
        pageLength={10}
        enableRowSelection
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems()}
          onClose={() => setRowMenu(null)}
          onPick={(key) => { const row = rowMenu.row; if (key === 'view') setViewRow(row); if (key === 'edit') setEditRow(row); if (key === 'delete') handleDelete(row); }}
        />
      )}

      <RecordDetailsModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        onEdit={() => { setEditRow(viewRow); setViewRow(null); }}
        onDelete={() => handleDelete(viewRow)}
        deleting={deleting}
        title="Department / Unit Details"
        subtitle={viewRow?.name}
        fields={viewRow ? [
          { label: 'Name',            value: viewRow.name || '-' },
          { label: 'Category',        value: viewRow.categoryLabel || '-' },
          { label: 'Department Name', value: viewRow.departmentName || '-' },
          { label: 'ID', value: viewRow.id, mono: true, wide: true },
        ] : []}
      />

      <CreateDepartmentModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        parentOptions={parentOptions}
      />

      <EditDepartmentModal
        isOpen={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
        row={editRow}
        parentOptions={parentOptions}
      />
    </section>
  );
}
